import { message } from 'antd'
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { cloneDeep, difference, isEmpty } from 'lodash-es'
import { arrayMove } from '@dnd-kit/sortable'

import {
  addFinanceProduct,
  archiveFinanceProduct,
  attachProductModifierGroup,
  deleteFinanceProduct,
  detachProductModifierGroup,
  moveProductModifierGroup,
  editFinanceProduct,
  editUnitConversion,
  getConversionProduct,
  getCountNoMpProduct,
  getFinanceProduct,
  getFinanceProductMpProducts,
  getFinanceProductMovementByCategoryChart,
  getFinanceProductPriceHistoryChartDetail,
  getFinanceProductRoles,
  getFinanceProducts,
  getFinanceProductSalesPurchaseChartDetail,
  getFinanceProductSerialNumbers,
  getFinanceProductsOverview,
  getFinanceProductStockMovementChart,
  getFinanceProductStockMovementChartDetail,
  getFinanceProductStockMovements,
  getFinanceProductTransactions,
  getFinanceProductValueMovementChart,
  getProductDetailSuggestion,
  getProductPerCategoryStats,
  getProductPinnedWarehouses,
  getProductsSummary,
  getProductSummary,
  getProductValuesDetails,
  getProductWarehouseQtyStats,
  getProfitabilityByProduct,
  getStockPerCategoryStats,
  getStockProduct,
  getStockProducts,
  getStockStatusStats,
  getSuggestionBarcodeFinanceProduct,
  getSuggestionFinanceProduct,
  getProductsPriceRule,
  isProductNameExist,
  pinProductWarehouses,
  unarchiveFinanceProduct,
  unpinProductWarehouse,
  updateProductWarehouseQuotas,
  uploadImportFinanceProductVariant,
  executeImportFinanceProductVariant,
} from 'utils/apis'
import { useSelector } from 'react-redux'
import { userSelector } from 'app-redux/selectors'
import { SUMMARY_STALE_TIME } from 'utils/summaryCache'
import { getProductSuggestionSerial } from '../apis'
import { useReportQuery } from './reports'

export const useProductsSuggestion = (payload, apiOptions, RQOptions) => {
  return useQuery(
    ['products-suggestion', payload],
    async () => {
      const {
        data: { data },
      } = await getSuggestionFinanceProduct(payload, apiOptions)
      return data
    },
    {
      ...RQOptions,
      keepPreviousData: true,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
    },
  )
}

export const useProducts = ({ payload = {}, enabled = false, options = {} }) => {
  // const options = useSelector(optionsSelector)
  // const dispatch = useDispatch()

  // // prevent user that already setting per_page > 100
  // if (payload.per_page > 100) {
  //   payload.per_page = 100
  // }

  // if (savePerPage && payload.per_page && payload.per_page !== options.products_per_page_default) {
  //   dispatch(
  //     editOption({
  //       products_per_page_default: payload.per_page,
  //     }),
  //   )
  // }

  return useQuery(
    ['product', payload],
    async () => {
      const {
        data: { data },
      } = await getFinanceProducts(payload)
      return data
    },
    {
      enabled,
      onError: (error) => {
        message.error(error?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useProductStockMovements = ({ payload = {}, options = {} }) => {
  return useQuery(
    ['productStockMovements', payload.id, payload],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductStockMovements(payload.id, payload.params)
      return data
    },
    {
      ...options,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load data from server!')
      },
    },
  )
}

export const useProductOverview = ({ payload = {}, enabled = false }) => {
  return useQuery(
    ['product', 'overview', payload],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductsOverview(payload)
      return data
    },
    {
      enabled,
      // keepPreviousData: true,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
    },
  )
}

export function useProductDetail({ id, params = {}, options, apiOptions = {} }) {
  return useQuery(
    ['product_detail', id],
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getFinanceProduct(id, params, apiOptions)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    options,
  )
}

export function useProduct({ id, payload = {}, enabled = false, options = {}, apiOptions = {} }) {
  const queryKey = ['product', id]
  if (!isEmpty(payload)) {
    queryKey.push(payload)
  }
  return useQuery(
    queryKey,
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getFinanceProduct(id, payload, apiOptions)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      enabled: !!id && enabled,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export function useConversionProduct(payload, options = {}) {
  return useQuery(
    ['productConvertion', payload.id],
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getConversionProduct(payload.id)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      enabled: !!payload.id,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductStockMovementChart = (payload, options = {}) => {
  return useQuery(
    ['productStockMovementChart', payload],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductStockMovementChart(payload)
      return data
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductMovementByCategoryChart = (payload, options = {}) => {
  return useQuery(
    ['productMovementByCategoryChart', payload],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductMovementByCategoryChart(payload)
      return data
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductValueMovementChart = (payload, options = {}) => {
  return useQuery(
    ['productValueMovementChart', payload],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductValueMovementChart(payload)
      return data
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export function useProductRoles({ id, enabled = false, options }) {
  return useQuery(
    ['product-roles', id],
    async () => {
      let response
      try {
        response = (await getFinanceProductRoles(id)).data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      enabled: !!id && enabled,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export function useSuggestionBarcodeProduct(payload, options = {}) {
  return useQuery(
    ['suggestionBarcodeProduct', payload],
    async () => {
      let response
      try {
        const { data } = await getSuggestionBarcodeFinanceProduct(payload)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    options,
  )
}

export const useMutateSuggestionBarcodeProduct = (options = {}) => {
  return useMutation(
    ({ apiOptions, ...payload }) => getSuggestionBarcodeFinanceProduct(payload, apiOptions),
    options,
  )
}

export const useProductSummary = (id, options = {}) => {
  return useQuery(
    ['productSummary', id],
    async () => {
      const {
        data: { data },
      } = await getProductSummary(id)
      return data
    },
    {
      enabled: !!id,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductRecommendedSummary = (id, options = {}) => {
  return useQuery(
    ['productSummary', 'recommended', id],
    async () => {
      const {
        data: { data },
      } = await getProductSummary(id, { type: 'recommended' })
      return data
    },
    {
      enabled: !!id,
      staleTime: 60_000,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductDetailSummary = (id, options = {}) => {
  return useQuery(
    ['productSummary', 'recommendedDetail', id],
    async () => {
      const {
        data: { data },
      } = await getProductSummary(id, { type: 'recommendedDetail' })
      return data
    },
    {
      enabled: !!id,
      staleTime: 60_000,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductReturnSummary = (id, options = {}) => {
  return useQuery(
    ['productSummary', 'recommendedReturn', id],
    async () => {
      const {
        data: { data },
      } = await getProductSummary(id, { type: 'recommendedReturn' })
      return data
    },
    {
      enabled: !!id,
      staleTime: 60_000,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductSalesPurchaseStats = (id, params = {}, options = {}) => {
  return useQuery(
    ['productSalesPurchaseStats', id, params],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductSalesPurchaseChartDetail(id, params)
      return data
    },
    {
      enabled: !!id,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductStockMovementStats = (id, params = {}, options = {}) => {
  return useQuery(
    ['productStockMovementStats', id, params],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductStockMovementChartDetail(id, params)
      return data
    },
    {
      enabled: !!id,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductPriceHistoryStats = (id, params = {}, options = {}) => {
  return useQuery(
    ['productPriceHistoryStats', id, params],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductPriceHistoryChartDetail(id, params)
      return data
    },
    {
      enabled: !!id,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductWarehouseQtyStats = (id, options = {}) => {
  return useQuery(
    ['productWarehouseQtyStats', id],
    async () => {
      const {
        data: { data },
      } = await getProductWarehouseQtyStats(id)
      return data
    },
    {
      enabled: !!id,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductDetailStockMovements = (id, params = {}, options = {}) => {
  return useQuery(
    ['productDetailStockMovements', id, params],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductStockMovements(id, params)
      return data
    },
    {
      enabled: !!id,
      onError: (error) => {
        message.error((error && error.message) || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useProductTransactions = (id, params = {}, options = {}) => {
  return useQuery(
    ['productTransactions', id, params],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductTransactions(id, params)
      return data
    },
    {
      enabled: !!id,
      onError: (error) => {
        message.error((error && error.message) || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useProductStock = (payload, options = {}) => {
  return useQuery(
    ['productStock', payload],
    async () => {
      const {
        data: { data },
      } = await getStockProduct(payload.id, payload.warehouse_ids, payload.trans_date)
      return data
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useUpdateProductWarehouseQuotas = () => {
  const queryClient = useQueryClient()
  return useMutation(({ id, ...payload }) => updateProductWarehouseQuotas(id, payload), {
    onSuccess: (response) => {
      if (response?.data?.success) {
        message.success(response.data.message)
      }
      queryClient.invalidateQueries(['productStock'])
    },
  })
}

export const useStockStatusStats = (payload, options = {}) => {
  return useQuery(
    ['stockStatusStats', payload],
    async () => {
      const {
        data: { data },
      } = await getStockStatusStats(payload)
      return data
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductsPriceRules = (params, options = {}, apiOptions = {}) => {
  return useQuery(
    ['productsPriceRules', params],
    async () => {
      const {
        data: { data },
      } = await getProductsPriceRule(params, apiOptions)
      return data || {}
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load price rules from server!'),
      ...options,
    },
  )
}

// Permintaan stok yang masuk di tick yang sama digabung jadi satu request per gudang + tanggal.
// Jadi query per pasangan produk-gudang tetap hemat request saat banyak baris dimuat sekaligus.
const pendingWarehouseStockLoads = new Map()
let warehouseStockFlushTimer = null

const flushWarehouseStockLoads = () => {
  const groups = [...pendingWarehouseStockLoads.values()]
  pendingWarehouseStockLoads.clear()
  warehouseStockFlushTimer = null

  groups.forEach((group) => {
    getStockProducts({
      product_ids: [...group.productIds].join(','),
      warehouse_ids: String(group.warehouseId),
      trans_date: group.transDate,
    })
      .then(({ data: { data } }) => {
        group.waiters.forEach(({ productId, resolve }) => {
          const row = (data || []).find((item) => String(item.id) === String(productId))
          resolve(row?.stocks?.[group.warehouseId]?.qty || 0)
        })
      })
      .catch((error) => {
        group.waiters.forEach(({ reject }) => reject(error))
      })
  })
}

const loadWarehouseStock = (productId, warehouseId, transDate) =>
  new Promise((resolve, reject) => {
    const key = `${warehouseId}|${transDate}`
    if (!pendingWarehouseStockLoads.has(key)) {
      pendingWarehouseStockLoads.set(key, {
        warehouseId,
        transDate,
        productIds: new Set(),
        waiters: [],
      })
    }
    const group = pendingWarehouseStockLoads.get(key)
    group.productIds.add(productId)
    group.waiters.push({ productId, resolve, reject })
    if (!warehouseStockFlushTimer) {
      warehouseStockFlushTimer = setTimeout(flushWarehouseStockLoads, 0)
    }
  })

// Stok per pasangan produk-gudang. Tambah bahan atau ganti gudang satu baris hanya memuat
// pasangan baru itu; baris lain tetap memakai cache. Hasil: { 'productId:warehouseId': { qty, isLoading } }.
export const useWarehouseStocks = ({ pairs, transDate }) => {
  const queries = useQueries(
    pairs.map(({ productId, warehouseId }) => ({
      queryKey: ['productWarehouseStock', { productId, warehouseId, transDate }],
      queryFn: () => loadWarehouseStock(productId, warehouseId, transDate),
      enabled: !!productId && !!warehouseId,
      onError: (error) =>
        message.error({
          key: 'productWarehouseStock',
          content: (error && error.message) || 'Failed to load stocks from server!',
        }),
    })),
  )

  return pairs.reduce((result, { productId, warehouseId }, index) => {
    result[`${productId}:${warehouseId}`] = {
      qty: queries[index]?.data,
      isLoading: queries[index]?.isLoading,
    }
    return result
  }, {})
}

export const useProductsStocks = (params, options = {}, apiOptions = {}) => {
  return useQuery(
    ['productsStocks', params],
    async () => {
      const {
        data: { data },
      } = await getStockProducts(params, apiOptions)
      return data || []
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load stocks from server!'),
      ...options,
    },
  )
}

export const useStockPerCategoryStats = (payload, options = {}) => {
  return useQuery(
    ['stockPerCategoryStats', payload],
    async () => {
      const {
        data: { data },
      } = await getStockPerCategoryStats(payload)
      return data
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useProductPerCategoryStats = (payload, options = {}) => {
  return useQuery(
    ['productPerCategoryStats', payload],
    async () => {
      const {
        data: { data },
      } = await getProductPerCategoryStats(payload)
      return data
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

// group: stock | sales | purchase (lihat dok BE cache summary produk).
// retry 1×: kegagalan sesaat di BE jangan langsung tampil sebagai kotak gagal.
export const useProductsSummary = ({ payload = {}, group }, options) =>
  useReportQuery(
    'productsSummary',
    getProductsSummary,
    { ...payload, cache: 1, groups: group },
    { retry: 1, staleTime: SUMMARY_STALE_TIME, ...options },
  )

export const useDeleteProduct = () => {
  return useMutation((payload) => deleteFinanceProduct(payload.id))
}

export const useUploadImportProductVariant = () => {
  return useMutation(uploadImportFinanceProductVariant)
}

export const useExecuteImportProductVariant = () => {
  return useMutation(executeImportFinanceProductVariant)
}

export const useArchiveProduct = () => {
  return useMutation((payload) => archiveFinanceProduct(payload.id))
}

export const useUnArchiveProduct = () => {
  return useMutation((payload) => unarchiveFinanceProduct(payload.id))
}

export const useEditProduct = () => {
  return useMutation((payload) => editFinanceProduct(payload))
}

export const useSaveProductModifierGroups = () => {
  return useMutation(({ id, selectedIds = [], existingIds = [] }) => {
    const added = difference(selectedIds, existingIds)
    const removed = difference(existingIds, selectedIds)
    return Promise.all([
      ...added.map((modifierGroupId) => attachProductModifierGroup(id, modifierGroupId)),
      ...removed.map((modifierGroupId) => detachProductModifierGroup(id, modifierGroupId)),
    ])
  })
}

export const useDetachProductModifierGroup = () => {
  return useMutation(({ id, modifierGroupId }) => detachProductModifierGroup(id, modifierGroupId))
}

export const useMoveProductModifierGroup = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ productId, srcId, destId }) => moveProductModifierGroup(productId, srcId, destId),
    {
      // Cache produk di-key dgn id dari route param (string), sedangkan productId di sini
      // number → key ['product', 123] tak match ['product', '123']. Pakai predicate agar
      // update optimistic mengenai query yang benar apa pun tipe id-nya.
      onMutate: async ({ productId, srcId, destId }) => {
        const filters = {
          predicate: (query) =>
            query.queryKey?.[0] === 'product' && String(query.queryKey?.[1]) === String(productId),
        }
        await queryClient.cancelQueries(filters)
        const previousData = queryClient.getQueriesData(filters)
        queryClient.setQueriesData(filters, (old) => {
          if (isEmpty(old?.modifier_groups)) {
            return old
          }
          const groups = old.modifier_groups
          const srcIndex = groups.findIndex((group) => String(group.id) === String(srcId))
          const destIndex = groups.findIndex((group) => String(group.id) === String(destId))
          if (srcIndex === -1 || destIndex === -1) {
            return old
          }
          const _old = cloneDeep(old)
          _old.modifier_groups = arrayMove(_old.modifier_groups, srcIndex, destIndex)
          return _old
        })
        return { previousData }
      },
      onSuccess: (response) => {
        message.success(response?.data?.message)
      },
      onError: (error, _vars, context) => {
        ;(context?.previousData || []).forEach(([key, data]) => {
          queryClient.setQueryData(key, data)
        })
        message.error(error?.data?.message)
      },
    },
  )
}

export const useAddProduct = () => {
  return useMutation((payload) => addFinanceProduct(payload))
}

export const useProductPinnedWarehouses = ({ id, options = {} } = {}) => {
  return useQuery(
    ['productPinnedWarehouses', id],
    async () => {
      const {
        data: { data },
      } = await getProductPinnedWarehouses(id)
      return data
    },
    {
      enabled: Boolean(id),
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const usePinProductWarehousesMutation = () => {
  return useMutation(({ id, warehouseIds }) =>
    pinProductWarehouses(id, { warehouse_ids: warehouseIds }),
  )
}

export const useUnpinProductWarehouseMutation = () => {
  return useMutation(({ id, warehouseId }) => unpinProductWarehouse(id, warehouseId))
}

export const useEditUnitConversion = () => {
  return useMutation(({ id, items }) => editUnitConversion(id, items))
}

export const useInfiniteProducts = (payload, options = {}) => {
  return useInfiniteQuery(
    ['infinite/product', payload],
    async ({ pageParam = 1 }) => {
      const {
        data: { data },
      } = await getFinanceProducts({ ...payload, page: pageParam })
      return data
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => message.error(error?.message || 'Failed to load data from server!'),
      keepPreviousData: true,
      ...options,
    },
  )
}

export const useInfiniteProductSuggestion = (payload, options = {}, apiOptions = {}) => {
  return useInfiniteQuery(
    ['infinite/products/suggestion', payload],
    async ({ pageParam = 0 }) => {
      let response
      try {
        const {
          data: { data },
        } = await getSuggestionFinanceProduct({ page: pageParam, ...payload }, apiOptions)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => message.error(error.message || 'Failed to load data from server!'),
      keepPreviousData: true,
      ...options,
    },
  )
}

export const useInfiniteProductSerialNumbersSuggestion = (
  payload,
  options = {},
  apiOptions = {},
) => {
  return useInfiniteQuery(
    ['infinite/products/serialnumbers/suggestion', payload],
    async ({ pageParam = 0 }) => {
      let response
      try {
        const {
          data: { data },
        } = await getFinanceProductSerialNumbers({ page: pageParam, ...payload }, apiOptions)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }

        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => message.error(error.message || 'Failed to load data from server!'),
      keepPreviousData: true,
      ...options,
    },
  )
}

export const useProductSerialNumbers = (payload, options) => {
  return useQuery(
    ['product/serial_numbers', payload.id, payload],
    async () => {
      const {
        data: { data },
      } = await getFinanceProductSerialNumbers(payload)
      return data
    },
    {
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export function useProductDetailSuggestion(id, params, options, apiOptions = {}) {
  return useQuery(
    ['product_detail_suggestion', id, params],
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getProductDetailSuggestion(id, params, apiOptions)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    options,
  )
}

export const useTotalProducts = (params, options) => {
  const { page, per_page, ...resParams } = params
  const _params = { ...resParams, include_total: 1 }
  return useQuery(
    ['total-products', _params],
    async () => {
      const {
        data: { data },
      } = await getProductsSummary(_params)
      return data
    },
    {
      onError: (error) => {
        message.error(error?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useProductValuesDetails = () => {
  const user = useSelector(userSelector)

  return useQuery(
    ['productValuesDetails', user?.lang],
    async () => {
      const {
        data: { data },
      } = await getProductValuesDetails()
      return data
    },
    {
      onError: (error) => {
        message.error((error && error.message) || 'Failed to load data from server!')
      },
    },
  )
}

export const useIsProductNameExistMutation = () => {
  return useMutation(isProductNameExist)
}

export const useProfitabilityByProduct = ({ payload, options }) => {
  return useQuery(
    ['profitabilityByProduct', payload],
    async () => {
      const {
        data: { data },
      } = await getProfitabilityByProduct({ params: payload })
      return data
    },
    {
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useProductSuggestionSerial = () => {
  return useMutation(({ apiOptions, ...data }) => getProductSuggestionSerial(data, apiOptions))
}

export const useCountNoMpProduct = (options = {}) => {
  return useQuery(
    ['countNoMpProduct'],
    async () => {
      const {
        data: { data },
      } = await getCountNoMpProduct()
      return data
    },
    {
      onError: (error) => {
        message.error(error?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const financeProductMpProductsQueryKey = (productId) => [
  'financeProductMpProducts',
  productId,
]

export const useFinanceProductMpProducts = ({ productId, enabled = false, options = {} }) => {
  return useQuery(
    financeProductMpProductsQueryKey(productId),
    async () => {
      const {
        data: { data },
      } = await getFinanceProductMpProducts(productId)
      return data?.items ?? []
    },
    {
      enabled: !!productId && enabled,
      staleTime: 0,
      onError: (error) => {
        message.error(error?.data?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}
